import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  BaseProfileFieldsValues,
  buildIdentityCardDefaultValues,
  buildProfileUpdatePayloadForIdentity,
  identityCardProfileSchema,
  IdentityFieldValues,
} from '@/pages/UserAccount/Profile';
import { FormHasDirtyFields } from '@/pages/UserAccount/Profile/ui/ProfilePresentational';

import BaseChapterTemplate from '@/features/ui/ProfileChapters/ui/BaseChapterTemplate/BaseChapterTemplate';

import { IntegrationSystem, SelectOption } from '@/shared/types';
import { FormSelect, FormTextInput } from '@/shared/ui';

import {
  allAllowedToChoosePassportDocumentTypeCodes,
  allowedToChooseOthersPassportDocumentTypeCodes,
  allowedToChooseUkrainePassportDocumentTypeCodes,
} from '@/components/Forms/constants';

import AuthContext from '@/contexts/Auth/AuthContext';

import { DocumentTypeEntity } from '@/api/types';

import api from '@/api';

import { isCountryUkraine, useUpdateIdentityCard, YesNoSelectOptions } from '../model';

import { UniversalDocumentFields } from './UniversalDocumentFields';

export const allowedToChoosePassportDocTypeStrategies = {
  ukrainian: (
    passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null,
  ): Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | undefined =>
    passportDocumentTypeEntities?.filter(
      (doc) => doc.code !== 'null' && allowedToChooseUkrainePassportDocumentTypeCodes.includes(doc.code),
    ),

  foreigner: (
    passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null,
  ): Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | undefined =>
    passportDocumentTypeEntities?.filter(
      (doc) => doc.code !== 'null' && allowedToChooseOthersPassportDocumentTypeCodes.includes(doc.code),
    ),

  any: (
    passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null,
  ): Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | undefined =>
    passportDocumentTypeEntities?.filter(
      (doc) => doc.code !== 'null' && allAllowedToChoosePassportDocumentTypeCodes.includes(doc.code),
    ),
};

const identityCardFields = [
  'withoutCitizenship',
  'citizenship',
  'passport',
  'passportDocTypeCode',
  'rnokpp',
  'documents',
] satisfies Array<keyof IdentityFieldValues | keyof BaseProfileFieldsValues>;

interface IdentityCardProps {
  passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  optionsCountry: SelectOption[];
  reinitTriggerCount: number;
  setFormHasDirtyFields: (value: FormHasDirtyFields) => void;
  activeKey: Array<NonNullable<FormHasDirtyFields>>;
}

export const IdentityCard: FC<IdentityCardProps> = ({
  passportDocumentTypeEntities,
  optionsCountry,
  setFormHasDirtyFields,
  activeKey,
}) => {
  const authContext = useContext(AuthContext);
  const defaultValues = useMemo(
    () => buildIdentityCardDefaultValues(authContext, { passportDocumentTypeEntities, optionsCountry }),
    [authContext, passportDocumentTypeEntities, optionsCountry],
  );
  const [triggerReinitCount, setTriggerReinitCount] = useState<number>(0);
  const [hasFormBeenTouched, setHasFormBeenTouched] = useState<boolean>(false);
  const methods = useForm<IdentityFieldValues>({
    resolver: zodResolver(identityCardProfileSchema) as any,
    reValidateMode: 'onChange',
    shouldFocusError: false,
    defaultValues: defaultValues,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { dirtyFields },
  } = methods;

  const onSubmit = async (values: IdentityFieldValues): Promise<void> => {
    const newProfileData = buildProfileUpdatePayloadForIdentity(values);
    try {
      const res = await api.updateUserProfile(newProfileData);
      authContext.updateUserData(res);
      setHasFormBeenTouched(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onReset = (): void => {
    methods.reset(defaultValues);
    setTriggerReinitCount((prev) => prev + 1);
    setHasFormBeenTouched(false);
  };

  useEffect(() => {
    if (!hasFormBeenTouched) {
      methods.reset(defaultValues);
      setTriggerReinitCount((prev) => prev + 1);
    }
  }, [defaultValues]);

  useUpdateIdentityCard({ methods, triggerReinitCount });

  const withoutCitizenship = watch('withoutCitizenship');
  const citizenship = watch('citizenship');

  const isIdentityCardFieldsDirty = Object.keys(dirtyFields).some((key) =>
    identityCardFields.includes(
      key as keyof Omit<IdentityFieldValues, 'passportDocTypeCode' | 'isWithoutCitizenshipUnset'>,
    ),
  );

  useEffect(() => {
    if (isIdentityCardFieldsDirty) {
      setHasFormBeenTouched(true);
    }
  }, [isIdentityCardFieldsDirty]);

  useEffect(() => {
    if (!hasFormBeenTouched) return setFormHasDirtyFields(null);

    setFormHasDirtyFields('identity');
  }, [hasFormBeenTouched]);

  useEffect(() => {
    if (!activeKey.includes('identity') && hasFormBeenTouched) onReset();
  }, [activeKey]);

  const isUkrainian = withoutCitizenship?.id === YesNoSelectOptions.NO.id && isCountryUkraine(citizenship?.id);

  const isForeigner =
    (withoutCitizenship?.id === YesNoSelectOptions.NO.id && !isCountryUkraine(citizenship?.id)) ||
    withoutCitizenship?.id === YesNoSelectOptions.YES.id;

  let docStrategy: keyof typeof allowedToChoosePassportDocTypeStrategies = 'any';
  if (isUkrainian) {
    docStrategy = 'ukrainian';
  } else if (isForeigner) {
    docStrategy = 'foreigner';
  }

  const allowedDocs = allowedToChoosePassportDocTypeStrategies[docStrategy](passportDocumentTypeEntities);
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BaseChapterTemplate
        fields={[
          <FormSelect
            options={Object.keys(YesNoSelectOptions).map(
              (key) => YesNoSelectOptions[key as keyof typeof YesNoSelectOptions] satisfies SelectOption,
            )}
            control={control}
            name="withoutCitizenship"
            label="Особа без Громадянства"
            key="withoutCitizenship"
            required
          />,
          withoutCitizenship?.id === YesNoSelectOptions.NO.id ? (
            <FormSelect
              options={optionsCountry}
              control={control}
              name="citizenship"
              label="Громадянство"
              key="citizenship"
              required
            />
          ) : null,
          <UniversalDocumentFields
            fieldName="passport"
            key="documentFields"
            documentTypeEntities={allowedDocs as SelectOption[]}
            layout="profile"
            documentRequired
            control={control}
            watch={watch}
          />,
          <FormTextInput control={control} name="rnokpp" label="РНОКПП" key="rnokpp" disabled />,
        ]}
        isDisabledSubmitAction={hasFormBeenTouched}
        onReset={onReset}
      />
    </form>
  );
};
